import {
  CALCULATION_PARAMS,
  TopLevelRow,
  isTopLevelBudgetRow
} from './post-optimization-constants';

/**
 * Calculate percentile of a sorted array
 * Uses linear interpolation between values
 */
export const percentile = (sortedArray: number[], p: number): number => {
  if (sortedArray.length === 0) {
    return 0;
  }
  if (sortedArray.length === 1) {
    return sortedArray[0];
  }

  const index = p * (sortedArray.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;

  return sortedArray[lower] * (1 - weight) + sortedArray[upper] * weight;
};

export interface WinsorBounds {
  qLow: number;
  qHigh: number;
}

/**
 * Compute winsorization percentile bounds for a group's metrics,
 * ignoring "no data" and must-excluded placements
 * Where qLow = 2nd percentile, qHigh = 98th percentile
 *
 * Returns undefined when the group has no valid metrics
 */
export const computeWinsorBounds = (
  allMetricsInGroup: (number | 'no data')[],
  mustExcludeFlags: boolean[]
): WinsorBounds | undefined => {
  const validMetrics = allMetricsInGroup.filter(
    (m, idx): m is number => m !== 'no data' && !mustExcludeFlags[idx]
  );

  if (validMetrics.length === 0) {
    return undefined;
  }

  const sorted = validMetrics.sort((a, b) => a - b);
  return {
    qLow: percentile(sorted, CALCULATION_PARAMS.winsorLowQ),
    qHigh: percentile(sorted, CALCULATION_PARAMS.winsorHighQ)
  };
};

/**
 * Winsorize metric by clamping to the group's percentile bounds
 * Formula: MIN(MAX(raw, qLow), qHigh)
 *
 * Special case: "no data" placements use qLow as fallback
 * This treats placements with missing data as minimum group performers
 */
export const winsorizeMetric = (
  rawMetric: number | 'no data',
  bounds: WinsorBounds | undefined
): number => {
  if (!bounds) {
    return typeof rawMetric === 'number' ? rawMetric : 0;
  }
  if (rawMetric === 'no data') {
    return bounds.qLow;
  }
  return Math.min(Math.max(rawMetric, bounds.qLow), bounds.qHigh);
};

/**
 * Calculate base attractiveness from winsorized metric
 * For ROAS metrics: Higher is better → MAX(winsorized, 0)
 * For CPX metrics: Lower is better → 1 / winsorized
 *
 * Note: winsorizedMetric of 0 means "no data" (NULL from SQL → "no data" string → 0 from winsorize)
 * This represents insufficient data to calculate the metric, not that the metric equals 0.
 * Placements with no data should not be allocated budget for that criteria.
 */
export const calculateBaseAttractiveness = (
  winsorizedMetric: number,
  allocationCriteria: string
): number => {
  if (winsorizedMetric === 0) {
    return 0;
  }

  const isRoas = allocationCriteria.toLowerCase().includes('roas');

  if (isRoas) {
    return Math.max(winsorizedMetric, 0);
  }
  // For CPX, lower is better, so invert
  return 1 / winsorizedMetric;
};

/**
 * Calculate ESS (Empirical Bayes Shrinkage)
 *
 * This applies Bayesian shrinkage to pull placements with low sample sizes
 * toward the group average, reducing variance in estimates.
 *
 * Formula: weight * baseAttractiveness + (1 - weight) * groupAvg + explorationBonus
 * Where weight = ess / (ess + shrinkK)
 *
 * @param baseAttractiveness - The placement's base attractiveness score
 * @param totalImpressions - From placementAnalytics.total_impressions (used as effective sample size)
 * @param groupAverageAttractiveness - Average base attractiveness across non-excluded placements in group
 */
export const calculateESS = (
  baseAttractiveness: number,
  totalImpressions: number,
  groupAverageAttractiveness: number
): number => {
  const ess = Math.max(CALCULATION_PARAMS.minEss, totalImpressions);
  const weight = ess / (ess + CALCULATION_PARAMS.shrinkK);

  return (
    weight * baseAttractiveness +
    (1 - weight) * groupAverageAttractiveness +
    CALCULATION_PARAMS.explorationEps
  );
};

/**
 * Calculate weighted share within group
 * Formula: essValue / SUM(essValues for non-excluded in group)
 * Returns 0 if mustExclude is true
 *
 * This normalizes ESS values to allocate budget proportionally.
 * If all non-excluded placements have ESS of 0 (division by zero), distributes budget equally among them.
 */
export const calculateWeightedShare = (
  essValue: number,
  mustExclude: boolean,
  eligibleEssSum: number,
  eligibleCount: number
): number => {
  if (mustExclude) {
    return 0;
  }

  if (eligibleEssSum > 0) {
    return essValue / eligibleEssSum;
  }

  // Fallback: equal distribution if all ESS values are 0
  return eligibleCount > 0 ? 1 / eligibleCount : 0;
};

/**
 * Calculate raw allocation before normalization
 * If mustInclude: MAX(minBook, weightedShare * groupBudget)
 * Else: weightedShare * groupBudget
 *
 * This ensures "Must Include" placements get at least the minimum booking
 */
export const calculateRawAllocation = (
  weightedShare: number,
  groupBudget: number,
  mustInclude: boolean
): number => {
  if (mustInclude) {
    return Math.max(CALCULATION_PARAMS.minBook, weightedShare * groupBudget);
  }
  return weightedShare * groupBudget;
};

/**
 * Calculate final allocation by normalizing raw allocations to sum to group budget
 * Formula: (rawAllocation / totalRawAllocations) * groupBudget
 *
 * This ensures the sum of allocations equals the group budget exactly
 */
export const calculateFinalAllocation = (
  rawAllocation: number,
  groupBudget: number,
  totalRawAllocation: number
): number => (totalRawAllocation > 0 ? (rawAllocation / totalRawAllocation) * groupBudget : 0);

/**
 * Calculate group average base attractiveness (excluding must-exclude items)
 * Used in ESS calculation for Bayesian shrinkage
 */
export const calculateGroupAverageAttractiveness = (
  baseAttractivenessValues: number[],
  mustExcludeFlags: boolean[]
): number => {
  const validValues = baseAttractivenessValues.filter((_, idx) => !mustExcludeFlags[idx]);
  if (validValues.length === 0) {
    return 0;
  }
  return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
};

/**
 * Distribute budgets from excluded top-level line items across every remaining
 * line item in proportion to its already-calculated final allocation.
 */
export const applyProportionalBudgetReallocation = (data: TopLevelRow[]): TopLevelRow[] => {
  const budgetToReallocate = data.reduce((total, row) => {
    if (isTopLevelBudgetRow(row) && row.mustExclude && row.shouldReallocateBudget) {
      return total + row.budget;
    }
    return total;
  }, 0);

  if (budgetToReallocate === 0) {
    return data;
  }

  const eligibleAllocationTotal = data.reduce((total, row) => {
    if (isTopLevelBudgetRow(row)) {
      return row.mustExclude ? total : total + (row.finalAllocation ?? 0);
    }
    return (
      total +
      row.subRows.reduce(
        (groupTotal, lineItem) =>
          lineItem.mustExclude ? groupTotal : groupTotal + (lineItem.finalAllocation ?? 0),
        0
      )
    );
  }, 0);

  if (eligibleAllocationTotal === 0) {
    // There is no proportional basis for redistribution, so leave the pool unallocated.
    return data;
  }

  const addReallocatedShare = (finalAllocation: number | undefined): number => {
    const currentAllocation = finalAllocation ?? 0;
    return currentAllocation + budgetToReallocate * (currentAllocation / eligibleAllocationTotal);
  };

  return data.map(row => {
    if (isTopLevelBudgetRow(row)) {
      return row.mustExclude
        ? row
        : { ...row, finalAllocation: addReallocatedShare(row.finalAllocation) };
    }

    return {
      ...row,
      subRows: row.subRows.map(lineItem =>
        lineItem.mustExclude
          ? lineItem
          : {
              ...lineItem,
              finalAllocation: addReallocatedShare(lineItem.finalAllocation)
            }
      )
    };
  });
};
